/* eslint-disable flights/no-use-effect-in-controller */
import { t, useI18n } from "@bookingcom/lingojs-react";
import useSearchFiltersController from "../../useSearchFiltersController";
import { useEffect, useState, useRef, useCallback } from "react";
import flights_web_budget_layover_airports_filters from "../../../../../utils/experiments/funnel/flights_web_budget_layover_airports_filters";
import debounce from "lodash/debounce";

const useDebouncedCallback = <T extends (...args: any[]) => any>(callback: T, delay: number) => {
  const debouncedFn = useRef<ReturnType<typeof debounce>>();
  useEffect(() => {
    debouncedFn.current = debounce((...args: Parameters<T>) => {
      callback(...args);
    }, delay);
    return () => {
      debouncedFn.current?.cancel();
    };
  }, [callback, delay]);
  return useCallback((...args: Parameters<T>) => {
    debouncedFn.current?.(...args);
  }, []);
};

export const useLayoverDurationFilterController = () => {
  const i18n = useI18n();
  const { layoverDuration } = useSearchFiltersController();
  const [value, setValue] = useState(layoverDuration.value);
  const lastCommittedValueRef = useRef(layoverDuration.value);

  useEffect(() => {
    setValue(layoverDuration.value);
    lastCommittedValueRef.current = layoverDuration.value;
  }, [layoverDuration.value]);

  const debouncedLayoverDurationSet = useDebouncedCallback(layoverDuration.set, 300);

  const handleOnChange = (value: number) => {
    flights_web_budget_layover_airports_filters.goals.interactedWithLayoverFilter();
    setValue(value);
  };

  const handleChangeCommit = (newValue: number) => {
    if (newValue !== lastCommittedValueRef.current) {
      lastCommittedValueRef.current = newValue;
      debouncedLayoverDurationSet(newValue); // Use debounced version instead of direct call
    }
  };

  return {
    isFilterAvailable: layoverDuration.options.max > 0 && layoverDuration.options.max !== layoverDuration.options.min,
    resetLabel: i18n.trans(t("flights_filter_reset")),
    showReset: layoverDuration.touched,
    onClickReset: layoverDuration.reset,
    min: layoverDuration.options.min,
    max: layoverDuration.options.max,
    value,
    ariaLabel: i18n.trans(t("a11y_flights_filter_layover_duration_announce")),
    label: i18n.trans(t("flights_filters_duration_layover_time")),
    onChange: (value: number) => handleOnChange(value),
    onChangeCommit: handleChangeCommit,
    renderTooltipValue: (value: number) =>
      i18n.trans(
        t("flights_filters_layover_hours", {
          variables: { num_hours: value.toString() },
          num_exception: value
        })
      ),
    renderValue: (value: number) =>
      i18n.trans(
        t("flights_filters_layover_hours", {
          variables: { num_hours: value.toString() },
          num_exception: value
        })
      ),
    keyForRemount: `layover-${layoverDuration.options.min}-${layoverDuration.options.max}`
  };
};
